import { Trans, useTranslation } from "react-i18next"
import {
  Text,
  Group,
  Badge,
  Rating,
  Button,
  Anchor,
  SimpleGrid,
  ThemeIcon,
  Paper,
  Stack,
} from "@mantine/core"
import { useUpdateVisitStar } from "../hooks/useAgencyVisits"
import { useUser } from "../../user/hooks/useUser"
import type { AgencyVisitDetail } from "../models/agencyVisit"
import { DetailSection, InfoRow } from "../../../components/crud"

const STATUS_MAP: Record<number, { label: string; color: string }> = {
  5: { label: "Agendada", color: "indigo" },
  10: { label: "Visitada", color: "teal" },
}

function EmptyValue() {
  return (
    <Text size="sm" fs="italic" c="gray.5">
      <Trans i18nKey="common.notFilled" />
    </Text>
  )
}

interface Props {
  visit: AgencyVisitDetail
}

export default function AgencyVisitDetailContent({ visit }: Props) {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageAgencyVisits ?? false
  const canRate = userData?.can?.setAgencyVisitStar ?? false
  const updateStar = useUpdateVisitStar()

return (
    <Stack>
      <Paper withBorder p="md">
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="lg">
          <InfoRow label={t("agencyVisits.agency")}>
            <Text fw={500}>{visit.agency_name}</Text>
          </InfoRow>
          <InfoRow label={t("agencyVisits.date")}>
            <Text fw={500}>
              {new Date(visit.date).toLocaleDateString("pt-PT", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </Text>
          </InfoRow>
          <InfoRow label={t("agencyVisits.status")}>
            <Badge color={STATUS_MAP[visit.status]?.color ?? "gray"} variant="light" size="sm">
              {STATUS_MAP[visit.status]?.label ?? visit.status}
            </Badge>
          </InfoRow>
          <InfoRow label={t("agencyVisits.rating")}>
            {canRate ? (
              <Rating
                value={visit.rating ?? 0}
                onChange={(val) => updateStar.mutate({ id: visit.id, rating: val })}
                size="md"
              />
            ) : (
              <Rating value={visit.rating ?? 0} readOnly size="sm" />
            )}
          </InfoRow>
          <InfoRow label={t("agencyVisits.visitors")}>
            <Text fw={500}>{visit.users.map((u) => u.fullName).join(", ") || <EmptyValue />}</Text>
          </InfoRow>
        </SimpleGrid>
      </Paper>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <DetailSection title={t("agencyVisits.agencyAppearance")}>
          <Text>{visit.agency_appearance || <EmptyValue />}</Text>
        </DetailSection>
        <DetailSection title={t("agencyVisits.technicalAspects")}>
          <Text>{visit.technical_aspects || <EmptyValue />}</Text>
        </DetailSection>
        <DetailSection title={t("agencyVisits.humanAspects")}>
          <Text>{visit.human_aspects || <EmptyValue />}</Text>
        </DetailSection>
        <DetailSection title={t("agencyVisits.comercialAspects")}>
          <Text>{visit.comercial_aspects || <EmptyValue />}</Text>
        </DetailSection>
      </SimpleGrid>

      <DetailSection title={t("agencyVisits.summary")}>
        <Text size="md">{visit.summary || <EmptyValue />}</Text>
      </DetailSection>

      <DetailSection title={t("agencyVisits.nextSteps")}>
        <Text size="md">{visit.next_steps || <EmptyValue />}</Text>
      </DetailSection>

      {canManage && visit.internal_notes && (
        <Paper withBorder p="md" style={{ borderLeft: "4px solid var(--mantine-color-red-5)" }}>
          <Text size="xs" tt="uppercase" fw={600} c="red" lts="0.05em" mb="sm">
            <Trans i18nKey="agencyVisits.internalNotes" />
          </Text>
          <Text>{visit.internal_notes}</Text>
        </Paper>
      )}

      {visit.medias && visit.medias.length > 0 && (
        <Paper withBorder p="md">
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb="md">
            <Trans i18nKey="agencyVisits.medias" /> ({visit.medias.length})
          </Text>
          <Stack gap="sm">
            {visit.medias.map((media) => (
              <Paper key={media.id} withBorder={false} bg="gray.0" p="sm" radius="md">
                <Group justify="space-between">
                  <Group>
                    <ThemeIcon variant="light" color="gray" size="md" radius="sm">
                      <i className={media.mime_class} style={{ fontSize: 14 }} />
                    </ThemeIcon>
                    <div>
                      <Text size="sm" fw={500}>{media.real_name}</Text>
                      <Text size="xs" c="dimmed">{media.mime_type}</Text>
                    </div>
                  </Group>
                  <Anchor href={media.url} target="_blank" size="sm">
                    <Button variant="subtle" size="compact-sm" leftSection={<i className="fa fa-download" />}>
                      <Trans i18nKey="common.download" />
                    </Button>
                  </Anchor>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}

    </Stack>
  )
}
